import constants from "../../../constants";

/**
 * Asset-type groups whose inspections can be regulatory (DVIR-bearing).
 *
 * Built-in group IDs are stable identifiers; group *names* are localizable display strings that a
 * customer can also reuse for their own group, so every asset-type decision goes through the ID.
 */
export const REGULATORY_ASSET_TYPE_GROUP_IDS: readonly string[] = [
    constants.GROUPS.VEHICLE_GROUP_ID,
    constants.GROUPS.TRAILER_GROUP_ID
];

/**
 * Item types that carry an administrator-set Regulated flag. Allowlisted rather than excluding
 * "OtherItem": the template editor saves Other items with isRegulated: true even though they have no
 * Regulated control, so counting by flag alone would make every template with an Other item
 * regulated. An item type added later stays non-regulated until someone opts it in here.
 */
const REGULATABLE_ITEM_TYPES: readonly string[] = ["CheckItem", "CustomProperty"];

/**
 * Whether this asset-type group can carry a regulatory inspection.
 *
 * Pass the set from getRegulatoryAssetTypeIds to include customer groups nested under a built-in.
 * An EMPTY set is treated as "not loaded", not as "nothing is regulatory": getRegulatoryAssetTypeIds
 * seeds the two built-ins on both its success and failure path, so size 0 can only be the Redux
 * slice's initial `[]`. Degrading to the built-ins keeps the common case right while the set loads.
 */
export function isRegulatoryAssetType(
    assetTypeId: string | undefined | null,
    regulatoryAssetTypeIds?: ReadonlySet<string>
): boolean {
    if (!assetTypeId) {
        return false;
    }
    return regulatoryAssetTypeIds && regulatoryAssetTypeIds.size > 0
        ? regulatoryAssetTypeIds.has(assetTypeId)
        : REGULATORY_ASSET_TYPE_GROUP_IDS.indexOf(assetTypeId) > -1;
}

/** The parts of an InspectionTemplate the regulatory rule reads, as returned by the generic Get. */
export interface IRegulatoryTemplate {
    inspectionParts?: { inspectionItems?: { itemType?: string; isRegulated?: boolean }[] | null }[] | null;
}

/** Whether any item an administrator can mark Regulated is marked Regulated. */
export function hasRegulatedItem(template: IRegulatoryTemplate): boolean {
    return (template.inspectionParts ?? []).some(part =>
        (part.inspectionItems ?? []).some(item =>
            REGULATABLE_ITEM_TYPES.indexOf(item.itemType ?? "") > -1 && item.isRegulated === true));
}

export interface IRegulatoryInspectionContext {
    /** Asset-type group ID of the inspected asset, as getAssetTypeId resolves it. */
    assetTypeId: string | null | undefined;
    /** Vehicle, Trailer and every group beneath them, from getRegulatoryAssetTypeIds. */
    regulatoryAssetTypeIds?: ReadonlySet<string>;
    /** The inspection's template, or null/undefined when it could not be read. */
    template: IRegulatoryTemplate | null | undefined;
}

/**
 * Whether an inspection is regulated: the inspected asset is a Vehicle, a Trailer or in
 * a group nested under either, and the template holds at least one regulated item. Certification
 * and the inspection type play no part. Computed live, because no inspection stores the answer.
 *
 * The asset term reads the asset actually inspected, not the template's asset-type selection: a
 * template covering Vehicle and Equipment must not regulate the Equipment inspected with it.
 *
 * Gates auto On Duty and all regulatory presentation.
 *
 * A template that could not be read passes the item term, so a lookup failure never hides a DVIR
 * from an officer.
 */
export function isRegulatoryInspection(context: IRegulatoryInspectionContext): boolean {
    if (!isRegulatoryAssetType(context.assetTypeId, context.regulatoryAssetTypeIds)) {
        return false;
    }
    return !context.template || hasRegulatedItem(context.template);
}
